import { supabaseServiceRole } from "src/lib/supabaseServiceClient";

export interface DemoRequest {
  id: string;
  parent_name: string;
  student_name: string;
  parent_phone: string | null;
  iso_country_code: string | null;
  phone_isd_code: string | null;
  email: string;
  grade: string | null;
  primary_goals: string[] | null;
  status: string;
  source: string | null;
  notes?: string | null;
  metadata?: any;
  created_at: string;
  updated_at?: string;
  assigned_admin?: string | null;
  demo_tutor?: string | null;
}

export interface FetchDemoRequestsOptions {
  status?: string;
  limit?: number;
}

export const demoRequestsService = {
  // Fetch recent 'new' demo requests limited (for dashboard widget)
  async fetchRecentNew(limit: number = 5): Promise<DemoRequest[]> {
    const { data, error } = await supabaseServiceRole
      .from("demo_requests")
      .select("id,parent_name,student_name,email,parent_phone,iso_country_code,phone_isd_code,grade,primary_goals,status,source,metadata,created_at")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) {
      console.error("Error fetching recent demo requests:", error);
      throw new Error(error.message || "Failed to fetch demo requests");
    }

    return data || [];
  },

  // Legacy fetchNewList alias
  async fetchNewList(limit: number = 100): Promise<DemoRequest[]> {
    return this.fetchList({ limit });
  },

  // Comprehensive fetch with optional status filtering
  async fetchList(options: FetchDemoRequestsOptions = {}): Promise<DemoRequest[]> {
    const { status, limit = 100 } = options;
    let query = supabaseServiceRole
      .from("demo_requests")
      .select("id,parent_name,student_name,email,parent_phone,iso_country_code,phone_isd_code,grade,primary_goals,status,source,notes,metadata,created_at,updated_at,assigned_admin,demo_tutor")
      .order("created_at", { ascending: false })
      .limit(limit);

    if (status && status !== "all") {
      query = query.eq("status", status);
    }

    const { data, error } = await query;

    if (error) {
      console.error("Error fetching demo requests list:", error);
      throw new Error(error.message || "Failed to fetch demo requests list");
    }

    return data || [];
  },

  // Update status and optional notes
  async updateStatus(id: string, status: string, notes?: string): Promise<DemoRequest> {
    const payload: Record<string, any> = {
      status,
      updated_at: new Date().toISOString(),
    };
    if (notes !== undefined) {
      payload.notes = notes;
    }

    const { data, error } = await supabaseServiceRole
      .from("demo_requests")
      .update(payload)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating status for demo request ${id}:`, error);
      throw new Error(error.message || "Failed to update demo request status");
    }

    return data;
  },

  // Update internal triage notes
  async updateNotes(id: string, notes: string): Promise<DemoRequest> {
    const { data, error } = await supabaseServiceRole
      .from("demo_requests")
      .update({
        notes,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error updating notes for demo request ${id}:`, error);
      throw new Error(error.message || "Failed to update notes");
    }

    return data;
  },

  // Assign demo tutor
  async assignTutor(id: string, tutorId: string | null): Promise<DemoRequest> {
    const { data, error } = await supabaseServiceRole
      .from("demo_requests")
      .update({
        demo_tutor: tutorId,
        updated_at: new Date().toISOString(),
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.error(`Error assigning tutor for demo request ${id}:`, error);
      throw new Error(error.message || "Failed to assign tutor");
    }

    return data;
  },
};
